import React from 'react'
import { observer } from 'mobx-react-lite'
import { ROUTES } from 'shared/config/routes'
import { ReportsShell } from '../components/ReportsShell/ReportsShell'
import { Toolbar } from '../components/Toolbar/Toolbar'
import { DecisionHero, HeroMetaItem } from '../components/DecisionHero/DecisionHero'
import { ReadinessRing } from '../components/ReadinessRing/ReadinessRing'
import { HorizontalBars } from '../components/HorizontalBars/HorizontalBars'
import { DistributionBar } from '../components/DistributionBar/DistributionBar'
import OptimizedCarousel from '../components/OptimizedCarousel/OptimizedCarousel'
import { ReportIcon } from '../components/ReportIcon/ReportIcon'
import { CaseloadPie } from './components/CaseloadPie/CaseloadPie'
import { useCounsellorReport, formatMinutes, initialsFor } from './hooks/useCounsellorReport'
import { buildCoveragePanels } from './helpers/coveragePanels'
import s from './styles/CounsellorReport.module.scss'

export const CounsellorReport = observer(() => {
	const {
		status,
		error,
		period,
		setPeriod,
		customFrom,
		setCustomFrom,
		customTo,
		setCustomTo,
		onApplyCustom,
		canApplyCustom,
		customRangeInvalid,
		triggerLabel,
		periodLabel,
		setView,
		isMine,
		rows,
		me,
		summary,
		coverage,
		activityRows,
		handleExport,
		tones,
		periods,
	} = useCounsellorReport()

	const coveragePanels = buildCoveragePanels({
		lifetime: me?.coverageLifetime,
		period: me?.coveragePeriod,
	})

	const teamIncomplete = summary.open
	const teamOverdue = summary.overdue
	const appsPerStudent =
		summary.caseload > 0
			? Math.round((summary.applications / summary.caseload) * 10) / 10
			: 0

	const recordedWorkRows = activityRows
		.filter(r => ['Completed sessions', 'Assignments given'].includes(r.label))
		.map(r => ({
			...r,
			tone: r.label === 'Completed sessions' ? 'green' : 'purple',
		}))

	const controls = (
		<Toolbar
			period={period}
			periods={periods}
			onPeriodChange={setPeriod}
			customFrom={customFrom}
			customTo={customTo}
			onCustomFromChange={setCustomFrom}
			onCustomToChange={setCustomTo}
			onApplyCustom={onApplyCustom}
			canApplyCustom={canApplyCustom}
			customRangeInvalid={customRangeInvalid}
			triggerLabel={triggerLabel}
			contextLabel={periodLabel}
			onExport={handleExport}
		/>
	)

	if (status === 'loading') {
		return (
			<ReportsShell title="Counsellor report" backTo={ROUTES.REPORTS} controls={controls}>
				<div className={s.empty} role="status">Loading counsellor insights…</div>
			</ReportsShell>
		)
	}

	if (status === 'idle') {
		return (
			<ReportsShell title="Counsellor report" backTo={ROUTES.REPORTS} controls={controls}>
				<div className={s.empty} role="status">
					Apply a valid custom date range to load this report.
				</div>
			</ReportsShell>
		)
	}

	if (status === 'error') {
		return (
			<ReportsShell title="Counsellor report" backTo={ROUTES.REPORTS} controls={controls}>
				<div className={s.empty} role="alert">
					Could not load counsellor insights{error ? `: ${error}` : '.'}
				</div>
			</ReportsShell>
		)
	}

	return (
		<ReportsShell title="Counsellor report" backTo={ROUTES.REPORTS} controls={controls}>
			<div className={s.tabs} role="tablist" aria-label="Counsellor report view">
				<button
					type="button"
					role="tab"
					aria-selected={isMine}
					className={`${s.tab} ${isMine ? s['tab--active'] : ''}`.trim()}
					onClick={() => setView('mine')}
				>
					<span className={s.tab__icon}>
						<ReportIcon name="person" className={s.fill} />
					</span>
					<span className={s.tab__text}>My work</span>
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={!isMine}
					className={`${s.tab} ${!isMine ? s['tab--active'] : ''}`.trim()}
					onClick={() => setView('team')}
				>
					<span className={s.tab__icon}>
						<ReportIcon name="groups" />
					</span>
					<span className={s.tab__text}>Team work</span>
				</button>
			</div>

			<DecisionHero
				eyebrow={isMine ? 'My Coverage' : 'Team Coverage'}
				title={`${summary.studentsWithSession} of ${summary.caseload} students had a completed session`}
				description={isMine
					? `${summary.open} incomplete tasks remain and ${summary.overdue} are overdue (current backlog).`
					: `Across ${rows.length} counsellors, ${teamIncomplete} incomplete tasks remain and ${teamOverdue} are overdue (current backlog).`
				}
				meta={
					<>
						<HeroMetaItem icon="event_available">
							{summary.sessions} completed sessions
						</HeroMetaItem>
						<HeroMetaItem icon="schedule">
							{formatMinutes(summary.minutes)} recorded session time
						</HeroMetaItem>
					</>
				}
				visual={<ReadinessRing value={coverage} label={isMine ? 'My Coverage' : 'Team Coverage'} />}
				tone={isMine ? 'success' : 'purple'}
			/>

			{isMine ? (
				<>
					<div className={s.cards}>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Total Students</span>
							<strong className={s.card__value}>{summary.caseload}</strong>
							<span className={s.card__meta}>Assigned caseload</span>
						</div>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Total Applications</span>
							<strong className={s.card__value}>{summary.applications}</strong>
							<span className={s.card__meta}>Applications created in period</span>
						</div>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Total Sessions</span>
							<strong className={`${s.card__value} ${s['card__value--purple']}`}>{summary.sessions}</strong>
							<span className={s.card__meta}>Completed sessions</span>
						</div>
					</div>

					<div className={s.split}>
						{coveragePanels.map(panel => (
							<section className={s.panel} key={panel.title}>
								<div className={s.panel__header}>
									<div>
										<h2 className={s.panel__title}>{panel.title}</h2>
										<p className={s.panel__subtitle}>{panel.label}</p>
									</div>
								</div>
								<div className={s.list}>
									{panel.metrics.map(m => (
										<div key={m.label} className={s.item}>
											<div className={`${s.item__icon} ${s[`item__icon--${m.tone}`]}`}>
												<ReportIcon name={m.icon} size="md" />
											</div>
											<div className={s.item__body}>
												<div className={s.item__top}>
													<strong className={s.item__label}>{m.label}</strong>
													<span className={s.item__value}>{m.val}%</span>
												</div>
												<div className={s.item__track}>
													<div
														className={`${s.item__fill} ${s[`tone--${m.tone}`] || ''}`.trim()}
														style={{ '--progress': `${m.val}%` }}
													/>
												</div>
											</div>
										</div>
									))}
								</div>
							</section>
						))}
					</div>

					<div className={s.cols}>
						<section className={s.panel}>
							<div className={s.panel__header}>
								<div className={s.panel__iconrow}>
									<span className={s.panel__icon}>
										<ReportIcon name="task_alt" />
									</span>
									<div>
										<h2 className={s.panel__title}>My Tasks &amp; Backlog</h2>
										<p className={s.panel__subtitle}>
											Completed in selected period vs currently open (not a completion rate).
										</p>
									</div>
								</div>
								{summary.overdue > 0 && (
									<span className={s.badge}>{summary.overdue} overdue.</span>
								)}
							</div>
							<DistributionBar
								label="Completed (period) vs Incomplete (current)"
								segments={[
									{ label: 'Completed tasks', value: summary.tasks, tone: 'green' },
									{ label: 'Incomplete tasks', value: summary.open, tone: 'orange' },
								]}
							/>
							<div className={s.summary}>
								<div className={s.summary__cell}>
									<span className={s.summary__label}>Completed tasks</span>
									<strong className={`${s.summary__value} ${s['summary__value--green']}`}>
										{summary.tasks}
									</strong>
								</div>
								<div className={s.summary__cell}>
									<span className={s.summary__label}>Incomplete tasks</span>
									<strong className={s.summary__value}>{summary.open}</strong>
								</div>
							</div>
						</section>

						<section className={s.panel}>
							<div className={s.panel__header}>
								<div className={s.panel__iconrow}>
									<span className={`${s.panel__icon} ${s['panel__icon--sm']}`}>
										<ReportIcon name="check" />
									</span>
									<h2 className={`${s.panel__title} ${s['panel__title--inline']}`}>
										My recorded work
									</h2>
								</div>
							</div>
							<HorizontalBars
								rows={recordedWorkRows}
								max={Math.max(...recordedWorkRows.map(row => row.value), 1)}
								ariaLabel="My recorded counselling work"
							/>
						</section>
					</div>
				</>
			) : (
				<>
					<div className={s.cards}>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Student/Counsellor Ratio</span>
							<strong className={s.card__value}>{Math.round(summary.caseload / (rows.length || 1))}:1</strong>
							<span className={s.card__meta}>Across {rows.length} active counsellors</span>
						</div>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Applications per Student</span>
							<strong className={s.card__value}>{appsPerStudent}</strong>
							<span className={s.card__meta}>Applications created in period / caseload</span>
						</div>
						<div className={`${s.panel} ${s.card}`}>
							<span className={s.card__label}>Completed / Incomplete Tasks</span>
							<strong className={`${s.card__value} ${s['card__value--purple']}`}>
								{summary.tasks}{' '}
								<span className={s.card__suffix}>
									/ {teamIncomplete}
								</span>
							</strong>
							<span className={s.card__meta}>Completed in period / currently open</span>
						</div>
					</div>

					<OptimizedCarousel
						type="team"
						title="Team Coverage Metrics"
						subtitle="Baseline engagement across the team (selected period)."
						rows={rows}
						tones={tones}
						initialsFor={initialsFor}
						views={[
							{
								title: 'Students with ≥1 Session',
								columnHeader: 'Coverage',
								renderRow: (row, i, ts) => {
									const count = Number(row.coveragePeriod?.withSession ?? row.studentsWithSession ?? 0)
									const pct = row.caseload
										? Math.min(100, Math.round((count / row.caseload) * 100))
										: 0
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{count}</strong>
												<span className={s.cell__pct}>{pct}%</span>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s[`tone--${ts[i % ts.length]}`] || ''}`.trim()}
													style={{ '--progress': `${pct}%` }}
												/>
											</div>
										</div>
									)
								},
							},
							{
								title: 'Students with ≥1 Application',
								columnHeader: 'Coverage',
								renderRow: (row, i, ts) => {
									const count = Number(row.coveragePeriod?.withApplication || 0)
									const pct = row.caseload
										? Math.min(100, Math.round((count / row.caseload) * 100))
										: 0
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{count}</strong>
												<span className={s.cell__pct}>{pct}%</span>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s[`tone--${ts[i % ts.length]}`] || ''}`.trim()}
													style={{ '--progress': `${pct}%` }}
												/>
											</div>
										</div>
									)
								},
							},
							{
								title: 'Students with ≥1 Assignment',
								columnHeader: 'Coverage',
								renderRow: (row, i, ts) => {
									const count = Number(row.coveragePeriod?.withAssignment || 0)
									const pct = row.caseload
										? Math.min(100, Math.round((count / row.caseload) * 100))
										: 0
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{count}</strong>
												<span className={s.cell__pct}>{pct}%</span>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s[`tone--${ts[i % ts.length]}`] || ''}`.trim()}
													style={{ '--progress': `${pct}%` }}
												/>
											</div>
										</div>
									)
								},
							},
						]}
					/>

					<div className={`${s.cols} ${s['cols--wide']}`}>
						<section className={s.panel}>
							<div className={s.panel__header}>
								<div>
									<h2 className={s.panel__title}>Task Completion</h2>
									<p className={s.panel__subtitle}>
										Completed in period vs currently incomplete (not a completion rate).
									</p>
								</div>
							</div>
							<div className={s.tasks__head}>
								<span className={s.tasks__cell}>Counsellor</span>
								<span className={s.tasks__cell}>Tasks (Done / Incomplete)</span>
							</div>
							<div className={s.tasks__body}>
								{rows.map(row => {
									const completed = Number(row.tasksCompleted || 0)
									const open = Number(row.tasksOpen || 0)
									const total = completed + open || 1
									return (
										<div className={s.tasks__row} key={row.id + 't'}>
											<div className={s.person}>
												<span className={s.person__avatar}>{initialsFor(row)}</span>
												<div>
													<strong className={s.person__name}>{row.name}</strong>
													<small className={s.person__meta}>{row.caseload} students</small>
												</div>
											</div>
											<div>
												<div className={s.counts}>
													<span>
														<strong className={s.counts__done}>{completed}</strong> done
													</span>
													<span>
														<strong className={s.counts__open}>{open}</strong> inc.
													</span>
												</div>
												<div className={s.progress}>
													<div
														className={s.progress__done}
														style={{ '--progress': `${(completed / total) * 100}%` }}
													/>
													<div
														className={s.progress__open}
														style={{ '--progress': `${(open / total) * 100}%` }}
													/>
												</div>
											</div>
										</div>
									)
								})}
							</div>
						</section>

						<section className={s.panel}>
							<div className={s.panel__header}>
								<div>
									<h2 className={s.panel__title}>Caseload Distribution</h2>
									<p className={s.panel__subtitle}>Student division amongst counsellors.</p>
								</div>
							</div>
							<CaseloadPie
								data={rows.map((r, i) => ({ label: r.name, value: Number(r.caseload || 0), tone: tones[i % tones.length] }))}
								centerValue={summary.caseload}
								centerLabel="Total Students"
							/>
						</section>
					</div>

					<OptimizedCarousel
						type="team"
						title="Team Output Totals"
						subtitle="Comparative bars (value vs max among counsellors)."
						rows={rows}
						tones={tones}
						initialsFor={initialsFor}
						views={[
							{
								title: 'Completed Sessions',
								columnHeader: 'Sessions',
								renderRow: (row) => {
									const val = Number(row.sessions || 0)
									const maxVal = Math.max(...rows.map(r => Number(r.sessions || 0)), 1)
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{val}</strong>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s['tone--green']}`.trim()}
													style={{ '--progress': `${(val / maxVal) * 100}%` }}
												/>
											</div>
										</div>
									)
								},
							},
							{
								title: 'Number of Assignments',
								columnHeader: 'Assignments',
								renderRow: (row) => {
									const val = Number(row.assignmentsGiven || 0)
									const maxVal = Math.max(...rows.map(r => Number(r.assignmentsGiven || 0)), 1)
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{val}</strong>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s['tone--orange']}`.trim()}
													style={{ '--progress': `${(val / maxVal) * 100}%` }}
												/>
											</div>
										</div>
									)
								},
							},
							{
								title: 'Number of Applications',
								columnHeader: 'Applications',
								renderRow: (row) => {
									const val = Number(row.applications || 0)
									const maxVal = Math.max(...rows.map(r => Number(r.applications || 0)), 1)
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{val}</strong>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s['tone--orange']}`.trim()}
													style={{ '--progress': `${(val / maxVal) * 100}%` }}
												/>
											</div>
										</div>
									)
								},
							},
							{
								title: 'Assigned Students',
								columnHeader: 'Students',
								renderRow: (row) => {
									const val = Number(row.caseload || 0)
									const maxVal = Math.max(...rows.map(r => Number(r.caseload || 0)), 1)
									return (
										<div className={s.cell}>
											<div className={s.cell__top}>
												<strong className={s.cell__value}>{val}</strong>
											</div>
											<div className={s.cell__track}>
												<span
													className={`${s.cell__fill} ${s['tone--blue']}`.trim()}
													style={{ '--progress': `${(val / maxVal) * 100}%` }}
												/>
											</div>
										</div>
									)
								},
							},
						]}
					/>
				</>
			)}

			<details className={s.note}>
				<summary className={s.note__summary}>
					<ReportIcon name="info" />
					How these numbers are counted
				</summary>
				<div className={s.note__body}>
					<dl className={s.note__dl}>
						<div>
							<dt className={s.note__dt}>Students with session</dt>
							<dd className={s.note__dd}>
								Assigned students with at least one non-deleted session note by this counsellor in the selected period. All time = no date filter. Coverage 100% = caseload.
							</dd>
						</div>
						<div>
							<dt className={s.note__dt}>Session minutes</dt>
							<dd className={s.note__dd}>
								Sum of session_notes.duration_ms in the period, converted to minutes. Missing/zero duration counts as 0.
							</dd>
						</div>
						<div>
							<dt className={s.note__dt}>Completed tasks</dt>
							<dd className={s.note__dd}>
								Task assignments completed in the selected period (completed_at). Incomplete and overdue are current snapshot state.
							</dd>
						</div>
						<div>
							<dt className={s.note__dt}>Task bar</dt>
							<dd className={s.note__dd}>
								100% = completed-in-period + currently-open segments. This is not a true completion rate.
							</dd>
						</div>
						<div>
							<dt className={s.note__dt}>Lifetime vs Period Coverage</dt>
							<dd className={s.note__dd}>
								Lifetime does not change with the period filter. Period Coverage follows the selected period (including All time).
							</dd>
						</div>
						<div>
							<dt className={s.note__dt}>Team output bars</dt>
							<dd className={s.note__dd}>
								Comparative only: value divided by the maximum among counsellors — not a completion percentage.
							</dd>
						</div>
					</dl>
				</div>
			</details>
		</ReportsShell>
	)
})
